/**
 * useAutosave — debounced autosave hook with localStorage backup.
 *
 * J-X12: Authoring auto-save (Test Composer, Question editor).
 *
 * Features:
 * - Debounced save (default 5s after last change)
 * - localStorage backup on each data change
 * - "Saved Xs ago" indicator via `savedAt` / `saveStatus`
 * - `beforeunload` warning when unsaved changes present
 * - Force-save via `saveNow()`
 *
 * Cross-cutting invariants:
 *   - Inv 12: no console.log/error/warn.
 *   - Inv 13: no `any` / `as any`.
 */

import { useEffect, useRef, useState, useCallback } from 'react';

export interface UseAutosaveOptions<T> {
  /** The data to autosave */
  data: T;
  /** The save callback — called with the current data */
  saveFn: (data: T) => Promise<unknown>;
  /** localStorage key: 'bimassess-autosave-<entityType>-<id>' */
  storageKey: string;
  /** Debounce interval in ms — default 5000 */
  debounceMs?: number;
  /** Whether autosave is active — default true */
  enabled?: boolean;
}

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface UseAutosaveReturn {
  /** Timestamp of last successful save */
  savedAt: Date | null;
  /** Currently saving */
  isSaving: boolean;
  /** Last error message */
  lastError: string | null;
  /** Current save status */
  saveStatus: SaveStatus;
  /** Force immediate save (bypass debounce) */
  saveNow: () => Promise<void>;
}

export function useAutosave<T>(options: UseAutosaveOptions<T>): UseAutosaveReturn {
  const { data, saveFn, storageKey, debounceMs = 5000, enabled = true } = options;

  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dataRef = useRef(data);
  const saveFnRef = useRef(saveFn);
  const hasUnsavedRef = useRef(false);
  const beforeUnloadHandlerRef = useRef<((e: BeforeUnloadEvent) => void) | null>(null);

  // Keep refs current
  dataRef.current = data;
  saveFnRef.current = saveFn;

  const performSave = useCallback(async (): Promise<void> => {
    setIsSaving(true);
    setSaveStatus('saving');
    setLastError(null);
    try {
      await saveFnRef.current(dataRef.current);
      setSavedAt(new Date());
      setSaveStatus('saved');
      hasUnsavedRef.current = false;
      // Remove beforeunload handler after successful save
      if (beforeUnloadHandlerRef.current) {
        window.removeEventListener('beforeunload', beforeUnloadHandlerRef.current);
        beforeUnloadHandlerRef.current = null;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Save failed';
      setLastError(message);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  }, []);

  // Write to localStorage on data change
  useEffect(() => {
    if (!enabled) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch {
      // localStorage may be full or unavailable — non-fatal
    }
  }, [data, storageKey, enabled]);

  // Debounce save on data change
  useEffect(() => {
    if (!enabled) return;

    // Mark as having unsaved changes
    hasUnsavedRef.current = true;

    // Register beforeunload if not already registered
    if (!beforeUnloadHandlerRef.current) {
      const handler = (e: BeforeUnloadEvent): void => {
        e.preventDefault();
        // Modern browsers require setting returnValue
        e.returnValue = '';
      };
      beforeUnloadHandlerRef.current = handler;
      window.addEventListener('beforeunload', handler);
    }

    // Clear existing timer and set new debounce
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      void performSave();
    }, debounceMs);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [data, enabled, debounceMs, performSave]);

  // Cleanup beforeunload on unmount
  useEffect(() => {
    return () => {
      if (beforeUnloadHandlerRef.current) {
        window.removeEventListener('beforeunload', beforeUnloadHandlerRef.current);
        beforeUnloadHandlerRef.current = null;
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const saveNow = useCallback(async (): Promise<void> => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    await performSave();
  }, [performSave]);

  return { savedAt, isSaving, lastError, saveStatus, saveNow };
}
